"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const Rest_1 = __importDefault(require("./Rest"));
const Restapi_1 = __importDefault(require("./paths/Restapi"));
const Scim_1 = __importDefault(require("./paths/Scim"));
const Analytics_1 = __importDefault(require("./paths/Analytics"));
const Ai_1 = __importDefault(require("./paths/Ai"));
const Rcvideo_1 = __importDefault(require("./paths/Rcvideo"));
const Webinar_1 = __importDefault(require("./paths/Webinar"));
const TeamMessaging_1 = __importDefault(require("./paths/TeamMessaging"));
class RingCentral {
    constructor(restOptions) {
        this.sdkExtensions = [];
        this.rest = new Rest_1.default(restOptions !== null && restOptions !== void 0 ? restOptions : {});
    }
    async installExtension(sdkExtension) {
        await sdkExtension.install(this);
        this.sdkExtensions.push(sdkExtension);
    }
    get token() {
        return this.rest.token;
    }
    set token(token) {
        this.rest.token = token;
    }
    async request(method, endpoint, content, queryParams, config) {
        try {
            const r = await this.rest.request(method, endpoint, content, queryParams, config);
            RingCentral.config.logger.info(`[${new Date().toLocaleString()} HTTP ${method} ${r.status} ${r.statusText}] ${this.rest.server} ${endpoint}`);
            return r;
        }
        catch (e) {
            const re = e;
            if (re.response) {
                const r = re.response;
                RingCentral.config.logger.info(`[${new Date().toLocaleString()} HTTP ${method} ${r.status} ${r.statusText}] ${this.rest.server} ${endpoint}`);
            }
            throw e;
        }
    }
    async get(endpoint, queryParams, config) {
        return this.request('GET', endpoint, undefined, queryParams, config);
    }
    async delete(endpoint, content, queryParams, config) {
        return this.request('DELETE', endpoint, content, queryParams, config);
    }
    async post(endpoint, content, queryParams, config) {
        return this.request('POST', endpoint, content, queryParams, config);
    }
    async put(endpoint, content, queryParams, config) {
        return this.request('PUT', endpoint, content, queryParams, config);
    }
    async patch(endpoint, content, queryParams, config) {
        return this.request('PATCH', endpoint, content, queryParams, config);
    }
    async getToken(getTokenRequest) {
        getTokenRequest.client_id = this.rest.clientId;
        this.token = await this.restapi(null).oauth().token().post(getTokenRequest);
        return this.token;
    }
    async authorize(options) {
        const getTokenRequest = {};
        if ('username' in options) {
            getTokenRequest.grant_type = 'password';
            getTokenRequest.username = options.username;
            getTokenRequest.extension = options.extension;
            getTokenRequest.password = options.password;
        }
        else if ('code' in options) {
            getTokenRequest.grant_type = 'authorization_code';
            getTokenRequest.code = options.code;
            getTokenRequest.redirect_uri = options.redirect_uri;
            // PKCE: https://medium.com/ringcentral-developers/use-authorization-code-pkce-for-ringcentral-api-in-client-app-e9108f04b5f0
            getTokenRequest.code_verifier = options.code_verifier;
        }
        else if ('jwt' in options) {
            getTokenRequest.grant_type = 'urn:ietf:params:oauth:grant-type:jwt-bearer';
            getTokenRequest.assertion = options.jwt;
        }
        else {
            throw new Error('Unsupported authorization flow');
        }
        return this.getToken(getTokenRequest);
    }
    /**
     * Each time you call token endpoint using this flow a new client session starts.
     * It is associated with the issued token pair: access token and refresh token, returned in response to this request.
     * To continue the session you can refresh the obtained access token and refresh token as many times as you need, using Refresh Token flow or the same flow.
     * To start another client session you should call token endpoint using this flow again.
     *
     * Please consider that only 5 simultaneously active sessions per extension per application are supported.
     * Thus if you exceed the number of sessions started per extension per application, the oldest one is ended.
     *
     * https://developers.ringcentral.com/api-reference/Get-Token
     *
     * @param options PasswordLoginFlowOpts
     */
    async login(options) {
        return this.authorize(options);
    }
    /**
     * Each time you call token endpoint using this flow, you continue current client session, and receive a new token pair: access token and refresh token in response to this request.
     * The old token pair immediately becomes inactive.
     *
     * https://developers.ringcentral.com/api-reference/Get-Token
     *
     * @param refreshToken Refresh Token
     */
    async refresh(refreshToken) {
        var _a;
        const tokenToRefresh = refreshToken !== null && refreshToken !== void 0 ? refreshToken : (_a = this.token) === null || _a === void 0 ? void 0 : _a.refresh_token;
        if (!tokenToRefresh) {
            throw new Error('tokenToRefresh must be specified.');
        }
        const getTokenRequest = {};
        getTokenRequest.grant_type = 'refresh_token';
        getTokenRequest.refresh_token = tokenToRefresh;
        return this.getToken(getTokenRequest);
    }
    /**
     * Each time you call token endpoint using this flow, you continue current client session, and receive a new token pair: access token and refresh token in response to this request.
     * The old token pair immediately becomes inactive.
     *
     * https://developers.ringcentral.com/api-reference/Revoke-Token
     *
     * @param tokenToRevoke AccessToken
     */
    async revoke(tokenToRevoke) {
        var _a, _b, _c;
        for (const sdkExtension of this.sdkExtensions) {
            await sdkExtension.revoke();
        }
        if (!tokenToRevoke && !this.token) {
            // nothing to revoke
            return;
        }
        if (!this.rest.clientId || !this.rest.clientSecret) {
            // no clientId or clientSecret, the token is from external source, cannot revoke
            return;
        }
        tokenToRevoke = (_b = tokenToRevoke !== null && tokenToRevoke !== void 0 ? tokenToRevoke : (_a = this.token) === null || _a === void 0 ? void 0 : _a.access_token) !== null && _b !== void 0 ? _b : (_c = this.token) === null || _c === void 0 ? void 0 : _c.refresh_token;
        await this.restapi(null).oauth().revoke().post({ token: tokenToRevoke });
        this.token = undefined;
    }
    restapi(apiVersion = 'v1.0') {
        return new Restapi_1.default(this, apiVersion);
    }
    scim(version = 'v2') {
        return new Scim_1.default(this, version);
    }
    analytics() {
        return new Analytics_1.default(this);
    }
    teamMessaging() {
        return new TeamMessaging_1.default(this);
    }
    ai() {
        return new Ai_1.default(this);
    }
    rcvideo() {
        return new Rcvideo_1.default(this);
    }
    webinar() {
        return new Webinar_1.default(this);
    }
}
RingCentral.config = {
    logger: {
        debug: () => { },
        log: () => { },
        info: () => { },
        warn: () => { },
        error: () => { },
    },
};
exports.default = RingCentral;
//# sourceMappingURL=index.js.map